import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Tooltip from '@mui/material/Tooltip'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'

import ChipButton from '@/components/ui/buttons/ChipButton'
import AvailabilityForm, { AvailabilityFormHandle, AvailabilityFormValues } from './AvailabilityForm'
import { StaffData } from './StaffSelectionTable'

export type UpdateAvailabilityDialogProps = {
  initialValues: AvailabilityFormValues
  isSubmitting?: boolean
  onSubmit: (values: AvailabilityFormValues) => void
  specialities: string[]
  staffs: StaffData[]
}

export function UpdateAvailabilityDialog(props: UpdateAvailabilityDialogProps) {
  const { initialValues, isSubmitting, onSubmit, specialities, staffs } = props
  const { t } = useTranslation()

  const formRef = useRef<AvailabilityFormHandle>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editable, setEditable] = useState(false)
  const [wasSubmitting, setWasSubmitting] = useState(false)

  useEffect(() => {
    if (isSubmitting) {
      setWasSubmitting(true)
    } else if (wasSubmitting) {
      setWasSubmitting(false)
      setEditable(false)
    }
  }, [isSubmitting, wasSubmitting])

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    handleReset()
  }

  const handleReset = () => {
    setEditable(false)
    formRef.current?.resetForm()
  }

  const handleSubmitForm = () => {
    formRef.current?.submitForm()
  }

  const toggleEditable = () => {
    setEditable(!editable)
  }

  return (
    <Box>
      <Tooltip title={t('page.meetingLink.updateAvailabilityDialog.openAvailabilityButton.tooltip')}>
        <ChipButton color="info" onClick={handleOpenDialog}>
          <EditRoundedIcon />
        </ChipButton>
      </Tooltip>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          {t('page.meetingLink.updateAvailabilityDialog.title')}
          <Tooltip title={t('common:button.close.tooltip')}>
            <IconButton onClick={handleCloseDialog}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <AvailabilityForm ref={formRef} initialValues={initialValues} onSubmit={onSubmit} readOnly={!editable} specialities={specialities} staffs={staffs} />
        </DialogContent>
        <DialogActions sx={{ p: (theme) => theme.spacing(2) }}>
          {editable ? (
            <>
              <Button onClick={handleReset} variant="neutral">{t('common:button.cancel.label')}</Button>
              <LoadingButton color="primary" disableElevation loading={isSubmitting} onClick={handleSubmitForm} variant="contained">
                {t('common:button.submit.label')}
              </LoadingButton>
            </>
          ) : (
            <Button onClick={toggleEditable} variant="contained">{t('common:button.modify.label')}</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UpdateAvailabilityDialog
